import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Loader2,
  CheckCircle,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Image,
  Upload,
  X,
  Rocket,
  Bot,
  Database,
  Sparkles,
  MessageCircle,
  TestTube,
  BarChart3,
  BookOpen,
  PlayCircle,
  GripVertical,
  Plus,
  Trash2,
} from 'lucide-react';
import { adminApi } from '../../services/adminApi';
import ConfirmDialog from '../../components/ui/ConfirmDialog';

// Icon mapping
const ICONS = {
  Rocket,
  Bot,
  Database,
  Sparkles,
  MessageCircle,
  TestTube,
  BarChart3,
  BookOpen,
  PlayCircle,
};

// Section defaults for icon and color (used for display only)
const SECTION_DEFAULTS = {
  'getting-started': { icon: 'Rocket', color: 'bg-green-500' },
  'agent-setup': { icon: 'Bot', color: 'bg-blue-500' },
  'knowledge-base': { icon: 'Database', color: 'bg-purple-500' },
  'features': { icon: 'Sparkles', color: 'bg-yellow-500' },
  'whatsapp': { icon: 'MessageCircle', color: 'bg-green-600' },
  'testing': { icon: 'PlayCircle', color: 'bg-orange-500' },
  'analytics': { icon: 'BarChart3', color: 'bg-cyan-500' },
};

function ArticleEditor({
  article,
  sectionId,
  onSave,
  onUploadImage,
  onDelete,
  onDragStart,
  onDragOver,
  onDrop,
  isDragging,
  isSaving,
  saveSuccess
}) {
  const [title, setTitle] = useState(article.title);
  const [content, setContent] = useState(article.content);
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isAiFormatting, setIsAiFormatting] = useState(false);
  const [pendingImage, setPendingImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [removeExistingImage, setRemoveExistingImage] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setTitle(article.title);
    setContent(article.content);
    setHasChanges(false);
    setPendingImage(null);
    setPreviewUrl(null);
    setRemoveExistingImage(false);
  }, [article]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleTitleChange = (e) => {
    setTitle(e.target.value);
    setHasChanges(true);
  };

  const handleContentChange = (e) => {
    setContent(e.target.value);
    setHasChanges(true);
  };

  const handleSave = async () => {
    await onSave(sectionId, article.id, { title, content });

    if (removeExistingImage && !pendingImage) {
      await onUploadImage(sectionId, article.id, null);
    } else if (pendingImage) {
      await onUploadImage(sectionId, article.id, pendingImage);
    }

    setHasChanges(false);
    setPendingImage(null);
    setPreviewUrl(null);
    setRemoveExistingImage(false);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setPreviewUrl(url);
      setPendingImage(file);
      setRemoveExistingImage(false);
      setHasChanges(true);
    }
    e.target.value = '';
  };

  const handleRemoveImage = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setPendingImage(null);
    setRemoveExistingImage(true);
    setHasChanges(true);
  };

  const handleAiFormat = async () => {
    if (!content.trim() || isAiFormatting) return;

    setIsAiFormatting(true);
    try {
      const result = await adminApi.formatWithAI(content, 'documentation');
      if (result.formatted) {
        setContent(result.formatted);
        setHasChanges(true);
      }
    } catch (err) {
      console.error('AI formatting error:', err);
    } finally {
      setIsAiFormatting(false);
    }
  };

  return (
    <div
      className={`border border-border rounded-lg overflow-hidden transition-all ${
        isDragging ? 'opacity-50 border-primary border-dashed' : ''
      }`}
      draggable
      onDragStart={(e) => onDragStart(e, article.id)}
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, article.id)}
    >
      <div className="flex items-center">
        <div
          className="p-4 cursor-grab active:cursor-grabbing hover:bg-bg-secondary/50 transition-colors"
          title="Drag to reorder"
        >
          <GripVertical className="w-4 h-4 text-text-muted" />
        </div>

        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex-1 flex items-center justify-between p-4 pl-0 hover:bg-bg-secondary/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-text-muted" />
            ) : (
              <ChevronRight className="w-4 h-4 text-text-muted" />
            )}
            <span className="font-medium text-text-primary">{article.title}</span>
            {hasChanges && (
              <span className="text-xs text-warning">• Unsaved changes</span>
            )}
          </div>
          {(article.screenshot || previewUrl) && !removeExistingImage && (
            <span className="text-xs text-success flex items-center gap-1">
              <Image className="w-3 h-3" />
              Has screenshot
            </span>
          )}
        </button>
      </div>

      {isExpanded && (
        <div className="p-4 border-t border-border space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Article Title
            </label>
            <input
              type="text"
              value={title}
              onChange={handleTitleChange}
              className="input w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Content
            </label>
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="flex items-center gap-1 p-2 bg-bg-secondary/50 border-b border-border">
                <button
                  type="button"
                  onClick={handleAiFormat}
                  disabled={!content.trim() || isAiFormatting}
                  className={`p-2 rounded flex items-center gap-1.5 transition-colors ${
                    content.trim() && !isAiFormatting
                      ? 'hover:bg-primary/10 text-text-secondary hover:text-primary cursor-pointer'
                      : 'text-text-muted/50 cursor-not-allowed'
                  }`}
                  title="Format with AI"
                >
                  {isAiFormatting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                  <span className="text-xs font-medium">Format with AI</span>
                </button>
                <span className="text-xs text-text-muted ml-auto">
                  Use **text** for bold, • for bullet points
                </span>
              </div>
              <textarea
                value={content}
                onChange={handleContentChange}
                rows={12}
                className="w-full p-3 font-mono text-sm focus:outline-none resize-y"
                placeholder="Write article content here..."
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Screenshot
            </label>
            {previewUrl ? (
              <div className="relative inline-block">
                <img
                  src={previewUrl}
                  alt="Screenshot preview"
                  className="max-w-md rounded-lg border border-border"
                />
                <span className="absolute top-2 left-2 px-2 py-1 bg-warning text-white text-xs rounded">
                  Unsaved
                </span>
                <button
                  onClick={handleRemoveImage}
                  className="absolute top-2 right-2 p-1 bg-danger text-white rounded-full hover:bg-danger/80"
                  title="Remove screenshot"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : article.screenshot && !removeExistingImage ? (
              <div className="relative inline-block">
                <img
                  src={article.screenshot}
                  alt="Article screenshot"
                  className="max-w-md rounded-lg border border-border"
                />
                <button
                  onClick={handleRemoveImage}
                  className="absolute top-2 right-2 p-1 bg-danger text-white rounded-full hover:bg-danger/80"
                  title="Remove screenshot"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="btn-secondary"
                >
                  <Upload className="w-4 h-4" />
                  Upload Screenshot
                </button>
              </div>
            )}
          </div>

          <div className="flex justify-between items-center">
            {!showDeleteConfirm ? (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="btn-secondary text-danger hover:bg-danger/10"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-sm text-text-muted">Delete this article?</span>
                <button
                  onClick={() => {
                    onDelete(sectionId, article.id);
                    setShowDeleteConfirm(false);
                  }}
                  className="btn-secondary text-danger hover:bg-danger/10 py-1 px-3"
                >
                  Yes
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="btn-secondary py-1 px-3"
                >
                  No
                </button>
              </div>
            )}

            <button
              onClick={handleSave}
              disabled={isSaving || !hasChanges}
              className="btn-primary"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : saveSuccess ? (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Saved!
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Article
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DocEditor() {
  const navigate = useNavigate();
  const [sections, setSections] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [savingArticle, setSavingArticle] = useState(null);
  const [saveSuccessArticle, setSaveSuccessArticle] = useState(null);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [draggedArticle, setDraggedArticle] = useState(null);
  const [draggedFromSection, setDraggedFromSection] = useState(null);
  const [draggedSection, setDraggedSection] = useState(null);
  const [showDeleteSectionDialog, setShowDeleteSectionDialog] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState(null);

  useEffect(() => {
    fetchDocs();
  }, []);

  const fetchDocs = async () => {
    try {
      setIsLoading(true);
      const response = await adminApi.getDocsStructure();
      if (response.sections && response.sections.length > 0) {
        setSections(response.sections);
      }
      // Don't set defaults - fetch from backend only
    } catch (err) {
      console.error('Failed to fetch docs:', err);
      setError('Failed to load documentation from server');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const handleSaveArticle = async (sectionId, articleId, data) => {
    const key = `${sectionId}-${articleId}`;
    try {
      setSavingArticle(key);
      setError(null);

      // Include section title for new sections
      const section = sections.find((s) => s.id === sectionId);
      const dataWithSectionTitle = {
        ...data,
        sectionTitle: section?.title || 'New Section',
      };

      await adminApi.updateDocArticle(sectionId, articleId, dataWithSectionTitle);

      setSections((prev) =>
        prev.map((section) =>
          section.id === sectionId
            ? {
                ...section,
                articles: section.articles.map((article) =>
                  article.id === articleId ? { ...article, ...data } : article
                ),
              }
            : section
        )
      );

      setSaveSuccessArticle(key);
      setTimeout(() => setSaveSuccessArticle(null), 2000);
    } catch (err) {
      console.error('Failed to save article:', err);
      setError('Failed to save article');
    } finally {
      setSavingArticle(null);
    }
  };

  const handleUploadImage = async (sectionId, articleId, file) => {
    const key = `${sectionId}-${articleId}`;
    try {
      setSavingArticle(key);

      if (file === null) {
        const section = sections.find((s) => s.id === sectionId);
        await adminApi.updateDocArticle(sectionId, articleId, {
          screenshot: null,
          sectionTitle: section?.title || 'New Section'
        });
        setSections((prev) =>
          prev.map((section) =>
            section.id === sectionId
              ? {
                  ...section,
                  articles: section.articles.map((article) =>
                    article.id === articleId ? { ...article, screenshot: null } : article
                  ),
                }
              : section
          )
        );
      } else {
        const section = sections.find((s) => s.id === sectionId);
        const response = await adminApi.uploadDocScreenshot(
          sectionId,
          articleId,
          file,
          section?.title || 'New Section'
        );
        setSections((prev) =>
          prev.map((section) =>
            section.id === sectionId
              ? {
                  ...section,
                  articles: section.articles.map((article) =>
                    article.id === articleId
                      ? { ...article, screenshot: response.url }
                      : article
                  ),
                }
              : section
          )
        );
      }

      setSaveSuccessArticle(key);
      setTimeout(() => setSaveSuccessArticle(null), 2000);
    } catch (err) {
      console.error('Failed to upload image:', err);
      setError('Failed to upload image');
    } finally {
      setSavingArticle(null);
    }
  };

  const handleAddArticle = (sectionId) => {
    const newArticle = {
      id: `article-${Date.now()}`,
      title: 'New Article',
      content: '',
      screenshot: null,
    };

    setSections((prev) =>
      prev.map((section) =>
        section.id === sectionId
          ? { ...section, articles: [...section.articles, newArticle] }
          : section
      )
    );
  };

  const handleDeleteArticle = (sectionId, articleId) => {
    setSections((prev) =>
      prev.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              articles: section.articles.filter((a) => a.id !== articleId),
            }
          : section
      )
    );
  };

  const handleDragStart = (e, sectionId, articleId) => {
    setDraggedArticle(articleId);
    setDraggedFromSection(sectionId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, sectionId, targetArticleId) => {
    e.preventDefault();

    if (!draggedArticle || draggedFromSection !== sectionId) {
      setDraggedArticle(null);
      setDraggedFromSection(null);
      return;
    }

    if (draggedArticle === targetArticleId) {
      setDraggedArticle(null);
      setDraggedFromSection(null);
      return;
    }

    setSections((prev) =>
      prev.map((section) => {
        if (section.id !== sectionId) return section;

        const articles = [...section.articles];
        const draggedIndex = articles.findIndex((a) => a.id === draggedArticle);
        const targetIndex = articles.findIndex((a) => a.id === targetArticleId);

        if (draggedIndex === -1 || targetIndex === -1) return section;

        const [draggedItem] = articles.splice(draggedIndex, 1);
        articles.splice(targetIndex, 0, draggedItem);

        return { ...section, articles };
      })
    );

    setDraggedArticle(null);
    setDraggedFromSection(null);
  };

  const handleDragEnd = () => {
    setDraggedArticle(null);
    setDraggedFromSection(null);
    setDraggedSection(null);
  };

  // Section drag handlers
  const handleSectionDragStart = (e, sectionId) => {
    setDraggedSection(sectionId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleSectionDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleSectionDrop = (e, targetSectionId) => {
    e.preventDefault();

    if (!draggedSection || draggedSection === targetSectionId) {
      setDraggedSection(null);
      return;
    }

    setSections((prev) => {
      const newSections = [...prev];
      const draggedIndex = newSections.findIndex((s) => s.id === draggedSection);
      const targetIndex = newSections.findIndex((s) => s.id === targetSectionId);

      if (draggedIndex === -1 || targetIndex === -1) return prev;

      const [draggedItem] = newSections.splice(draggedIndex, 1);
      newSections.splice(targetIndex, 0, draggedItem);

      return newSections;
    });

    setDraggedSection(null);
  };

  const handleAddSection = () => {
    const newSection = {
      id: `section-${Date.now()}`,
      title: 'New Section',
      articles: [],
    };
    setSections((prev) => [...prev, newSection]);
  };

  const handleDeleteSection = (sectionId) => {
    setSections((prev) => prev.filter((s) => s.id !== sectionId));
  };

  const handleUpdateSectionTitle = (sectionId, newTitle) => {
    setSections((prev) =>
      prev.map((section) =>
        section.id === sectionId ? { ...section, title: newTitle } : section
      )
    );
  };

  const handleSaveAll = async () => {
    try {
      setSavingArticle('all');
      setError(null);
      await adminApi.saveDocsStructure(sections);
      setSaveSuccessArticle('all');
      setTimeout(() => setSaveSuccessArticle(null), 2000);
    } catch (err) {
      console.error('Failed to save all:', err);
      setError('Failed to save documentation');
    } finally {
      setSavingArticle(null);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 lg:p-8 max-w-5xl mx-auto">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-bg-secondary rounded w-48"></div>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 bg-bg-secondary rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/docs')}
            className="p-2 rounded-lg hover:bg-bg-secondary transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-text-primary">
              Documentation Editor
            </h1>
            <p className="text-text-secondary text-sm">
              Edit help articles shown to users
            </p>
          </div>
        </div>
        <button
          onClick={handleSaveAll}
          disabled={savingArticle === 'all'}
          className="btn-primary"
        >
          {savingArticle === 'all' ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Saving All...
            </>
          ) : saveSuccessArticle === 'all' ? (
            <>
              <CheckCircle className="w-4 h-4" />
              All Saved!
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Save All
            </>
          )}
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-danger/10 border border-danger/20 rounded-lg flex items-center gap-3 text-danger">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{error}</p>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {sections.length === 0 ? (
        <div className="card p-8 text-center">
          <p className="text-text-muted">No documentation sections found. Please check the backend configuration.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {sections.map((section) => {
            let IconComponent = Rocket;
            let sectionColor = 'bg-primary';

            const title = section.title?.toLowerCase() || '';
            if (title.includes('getting started')) {
              IconComponent = Rocket;
              sectionColor = 'bg-green-500';
            } else if (title.includes('agent')) {
              IconComponent = Bot;
              sectionColor = 'bg-blue-500';
            } else if (title.includes('knowledge')) {
              IconComponent = Database;
              sectionColor = 'bg-purple-500';
            } else if (title.includes('feature')) {
              IconComponent = Sparkles;
              sectionColor = 'bg-yellow-500';
            } else if (title.includes('whatsapp')) {
              IconComponent = MessageCircle;
              sectionColor = 'bg-green-600';
            } else if (title.includes('test')) {
              IconComponent = PlayCircle;
              sectionColor = 'bg-orange-500';
            } else if (title.includes('analytics') || title.includes('report')) {
              IconComponent = BarChart3;
              sectionColor = 'bg-cyan-500';
            }

            const isExpanded = expandedSections[section.id];

            return (
              <div
                key={section.id}
                className={`card overflow-hidden transition-all ${
                  draggedSection === section.id ? 'opacity-50 border-primary border-dashed' : ''
                }`}
                draggable
                onDragStart={(e) => handleSectionDragStart(e, section.id)}
                onDragOver={handleSectionDragOver}
                onDrop={(e) => handleSectionDrop(e, section.id)}
                onDragEnd={handleDragEnd}
              >
                <div className="flex items-center">
                  <div
                    className="p-4 cursor-grab active:cursor-grabbing hover:bg-bg-secondary/50 transition-colors"
                    title="Drag to reorder section"
                  >
                    <GripVertical className="w-5 h-5 text-text-muted" />
                  </div>
                  <button
                    onClick={() => toggleSection(section.id)}
                    className="flex-1 flex items-center justify-between p-4 pl-0 hover:bg-bg-secondary/30 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg ${sectionColor} flex items-center justify-center`}>
                        <IconComponent className="w-5 h-5 text-white" />
                      </div>
                      <div className="text-left">
                        <h2 className="font-semibold text-text-primary">{section.title}</h2>
                        <p className="text-sm text-text-muted">
                          {section.articles?.length || 0} article{(section.articles?.length || 0) !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-text-muted" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-text-muted" />
                    )}
                  </button>
                </div>

                {isExpanded && (
                  <div className="p-4 space-y-3" onDragEnd={handleDragEnd}>
                    {/* Section Settings */}
                    <div className="p-3 bg-bg-secondary/30 rounded-lg space-y-3">
                      <div className="flex items-center gap-3">
                        <label className="text-sm font-medium text-text-primary whitespace-nowrap">
                          Section Title:
                        </label>
                        <input
                          type="text"
                          value={section.title}
                          onChange={(e) => handleUpdateSectionTitle(section.id, e.target.value)}
                          className="input flex-1"
                          placeholder="Section title"
                        />
                        <button
                          onClick={() => {
                            setSectionToDelete(section);
                            setShowDeleteSectionDialog(true);
                          }}
                          className="btn-secondary text-danger hover:bg-danger/10 flex-shrink-0"
                          title="Delete section"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete Section
                        </button>
                      </div>
                    </div>

                    {/* Articles */}
                    {section.articles?.map((article) => (
                      <ArticleEditor
                        key={article.id}
                        article={article}
                        sectionId={section.id}
                        onSave={handleSaveArticle}
                        onUploadImage={handleUploadImage}
                        onDelete={handleDeleteArticle}
                        onDragStart={(e, articleId) => handleDragStart(e, section.id, articleId)}
                        onDragOver={handleDragOver}
                        onDrop={(e, targetArticleId) => handleDrop(e, section.id, targetArticleId)}
                        isDragging={draggedArticle === article.id}
                        isSaving={savingArticle === `${section.id}-${article.id}`}
                        saveSuccess={saveSuccessArticle === `${section.id}-${article.id}`}
                      />
                    ))}

                    <button
                      onClick={() => handleAddArticle(section.id)}
                      className="w-full p-3 border-2 border-dashed border-border rounded-lg text-text-muted hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Add Article
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          {/* Add Section Button */}
          <button
            onClick={handleAddSection}
            className="w-full p-4 border-2 border-dashed border-border rounded-lg text-text-muted hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Add New Section
          </button>
        </div>
      )}

      {/* Add Section Button when no sections */}
      {sections.length === 0 && (
        <button
          onClick={handleAddSection}
          className="w-full p-4 border-2 border-dashed border-border rounded-lg text-text-muted hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-2 mt-4"
        >
          <Plus className="w-5 h-5" />
          Add New Section
        </button>
      )}

      {/* Delete Section Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteSectionDialog}
        onClose={() => {
          setShowDeleteSectionDialog(false);
          setSectionToDelete(null);
        }}
        onConfirm={() => {
          if (sectionToDelete) {
            handleDeleteSection(sectionToDelete.id);
          }
          setShowDeleteSectionDialog(false);
          setSectionToDelete(null);
        }}
        title="Delete Section"
        message={`Are you sure you want to delete "${sectionToDelete?.title || 'this section'}" and all its articles? This action cannot be undone.`}
        confirmText="Delete Section"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
}
